const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';

/**
 * Adds desktop popover dismissal and focus behavior to a cart-line grout disclosure.
 * The native details element remains the interaction model on mobile and in the cart drawer.
 */
class CartLineGrout extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MediaQueryList | null} */
  #desktopMediaQuery = null;

  connectedCallback() {
    if (this.dataset.cartContext !== 'page') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const { signal } = this.#abortController;
    this.details?.addEventListener('toggle', this.#handleToggle, { signal });
    this.closeButton?.addEventListener('click', this.#handleCloseClick, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.#desktopMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#desktopMediaQuery = null;
  }

  #handleToggle = () => {
    if (!this.isDesktop || !this.details?.open) return;

    for (const grout of document.querySelectorAll('cart-line-grout[data-cart-context="page"]')) {
      if (grout === this || !(grout instanceof CartLineGrout)) continue;
      grout.close(false);
    }
  };

  /** @param {MouseEvent} event */
  #handleCloseClick = (event) => {
    event.preventDefault();
    this.close(true);
  };

  /** @param {PointerEvent} event */
  #handleOutsidePointerDown = (event) => {
    if (!this.isDesktop || !this.details?.open || !(event.target instanceof Node) || this.contains(event.target)) {
      return;
    }

    this.close(true);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (!this.isDesktop || !this.details?.open || event.key !== 'Escape') return;

    event.preventDefault();
    this.close(true);
  };

  #handleBreakpointChange = () => {
    if (!this.isDesktop || !this.details?.open) return;

    for (const details of document.querySelectorAll('cart-line-grout[data-cart-context="page"] details[open]')) {
      const owner = details.closest('cart-line-grout');
      if (owner !== this && details instanceof HTMLDetailsElement) details.open = false;
    }
  };

  /** @param {boolean} restoreFocus */
  close(restoreFocus = true) {
    const details = this.details;
    if (!details?.open) return;

    details.open = false;
    if (restoreFocus) this.summary?.focus({ preventScroll: true });
  }

  get isDesktop() {
    return this.#desktopMediaQuery?.matches === true;
  }

  get details() {
    const details = this.querySelector('.cart-line-grout__details');
    return details instanceof HTMLDetailsElement ? details : null;
  }

  get summary() {
    const summary = this.querySelector('.cart-line-grout__header');
    return summary instanceof HTMLElement ? summary : null;
  }

  get closeButton() {
    const button = this.querySelector('[data-cart-line-grout-close]');
    return button instanceof HTMLButtonElement ? button : null;
  }
}

if (!customElements.get('cart-line-grout')) {
  customElements.define('cart-line-grout', CartLineGrout);
}
